import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { StatTile } from '@/components/ui/stat-tile';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatMinor } from '@/hooks/use-shifts';
import {
  PROMOTION_DISCOUNT_REPORT_MAX_DAYS,
  downloadPromotionDiscountCsv,
  usePromotionDiscountReport,
  usePromotionDiscountReportFilters,
  type PromotionDiscountReportScope,
} from '@/hooks/use-promotion-discount-report';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

/** A local calendar date as YYYY-MM-DD. */
function localIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Calendar arithmetic on a date label; no zone is involved. */
function dayNumber(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function shiftDays(isoDate: string, days: number): string {
  return new Date(dayNumber(isoDate) + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * EAT-3 — Reports → Promotion discounts. Promotion discount given per store,
 * business day and item, from what the tills already upload, with totals per
 * store and day; both tables downloadable as CSV.
 *
 * The dates are BUSINESS days: the server reads them on the tenant's wall
 * clock (the same zone as the dashboard), not the viewer's, and says which
 * zone it used. The scope lives in the URL, so refresh, sharing and Back land
 * on the same report.
 */
export function PromotionDiscountReportPage() {
  const t = useT('admin_ui');
  const [params, setParams] = useSearchParams();
  const today = localIso(new Date());
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const fromDate =
    fromParam !== null && ISO_DATE.test(fromParam) ? fromParam : shiftDays(today, -6);
  const toDate = toParam !== null && ISO_DATE.test(toParam) ? toParam : today;
  const locationId = params.get('locationId') ?? '';
  const onlyPromoted = params.get('onlyPromoted') === 'true';

  const update = (next: Record<string, string>): void => {
    const merged: Record<string, string> = {
      from: fromDate,
      to: toDate,
      locationId,
      onlyPromoted: onlyPromoted ? 'true' : '',
      ...next,
    };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')));
  };

  const days = Math.round((dayNumber(toDate) - dayNumber(fromDate)) / DAY_MS) + 1;
  const rangeError =
    days < 1
      ? t('reports.promotionDiscounts.rangeReversed', 'The end date is before the start date.')
      : days > PROMOTION_DISCOUNT_REPORT_MAX_DAYS
        ? t('reports.promotionDiscounts.rangeTooLong', 'Choose at most {days} days.').replace(
            '{days}',
            String(PROMOTION_DISCOUNT_REPORT_MAX_DAYS),
          )
        : null;

  const scope: PromotionDiscountReportScope | null =
    rangeError !== null
      ? null
      : {
          fromDate,
          toDate,
          ...(locationId ? { locationId } : {}),
          ...(onlyPromoted ? { onlyPromoted: true } : {}),
        };

  const filters = usePromotionDiscountReportFilters();
  const { data, isLoading, error } = usePromotionDiscountReport(scope);
  const [downloading, setDownloading] = useState<'items' | 'days' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = async (view: 'items' | 'days'): Promise<void> => {
    if (scope === null) return;
    setDownloading(view);
    setDownloadError(null);
    try {
      const prefix = view === 'days' ? 'promotion-discounts-days' : 'promotion-discounts';
      await downloadPromotionDiscountCsv(scope, view, `${prefix}_${fromDate}_${toDate}.csv`);
    } catch (err) {
      setDownloadError(extractApiError(err));
    } finally {
      setDownloading(null);
    }
  };

  const store = (code: string | null, name: string | null): string =>
    code === null
      ? t('reports.promotionDiscounts.noStore', 'No store')
      : name === null
        ? code
        : `${code} · ${name}`;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">
          {t('reports.promotionDiscounts.title', 'Promotion discounts')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'reports.promotionDiscounts.intro',
            'Promotion discount given per store, business day and item, from the sales the tills uploaded. It shows how much discount promotions gave, not which promotion gave it.',
          )}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="promo-from">{t('reports.promotionDiscounts.from', 'From')}</Label>
          <Input
            id="promo-from"
            type="date"
            value={fromDate}
            onChange={(e) => ISO_DATE.test(e.target.value) && update({ from: e.target.value })}
            className="max-w-[180px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="promo-to">{t('reports.promotionDiscounts.to', 'To')}</Label>
          <Input
            id="promo-to"
            type="date"
            value={toDate}
            onChange={(e) => ISO_DATE.test(e.target.value) && update({ to: e.target.value })}
            className="max-w-[180px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="promo-location">{t('reports.promotionDiscounts.store', 'Store')}</Label>
          <Select
            id="promo-location"
            value={locationId}
            onChange={(e) => update({ locationId: e.target.value })}
            className="min-w-[200px]"
          >
            <option value="">{t('reports.promotionDiscounts.allStores', 'All stores')}</option>
            {(filters.data?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.code} · {l.name}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={onlyPromoted}
            onChange={(e) => update({ onlyPromoted: e.target.checked ? 'true' : '' })}
            data-testid="promo-only-promoted"
          />
          {t('reports.promotionDiscounts.onlyPromoted', 'Only items with promotion discount')}
        </label>
      </div>

      {rangeError !== null && (
        <p className="text-sm text-destructive" role="alert">
          {rangeError}
        </p>
      )}
      {error !== null && (
        <p className="text-sm text-destructive" role="alert">
          {extractApiError(error)}
        </p>
      )}
      {data !== undefined && (
        <p className="text-xs text-muted-foreground" data-testid="promo-zone-note">
          {data.timeZoneAmbiguous
            ? t(
                'reports.promotionDiscounts.zoneAmbiguous',
                'Locations declare more than one timezone, so business days are read in UTC.',
              )
            : t(
                'reports.promotionDiscounts.zone',
                'Business days are read in {zone}, as on the dashboard.',
              ).replace('{zone}', data.timeZone)}
        </p>
      )}

      {data !== undefined &&
        data.totals.map((total) => (
          <section
            key={total.currency ?? ''}
            className="grid grid-cols-2 gap-4 sm:grid-cols-4"
            data-testid="promo-totals"
          >
            <StatTile
              label={t('reports.promotionDiscounts.promotionDiscount', 'Promotion discount')}
              value={formatMinor(total.promotionDiscountMinor, total.currency)}
              highlight
            />
            <StatTile
              label={t('reports.promotionDiscounts.gross', 'Gross before discount')}
              value={formatMinor(total.grossMinor, total.currency)}
            />
            <StatTile
              label={t('reports.promotionDiscounts.otherDiscount', 'Other line discount')}
              value={formatMinor(total.otherDiscountMinor, total.currency)}
            />
            <StatTile
              label={t('reports.promotionDiscounts.net', 'Net')}
              value={formatMinor(total.netMinor, total.currency)}
            />
          </section>
        ))}

      {downloadError !== null && (
        <p className="text-sm text-destructive" role="alert">
          {downloadError}
        </p>
      )}

      {/* ---------- Per store and day ---------- */}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {t('reports.promotionDiscounts.perDay', 'Per store and day')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            disabled={scope === null || downloading !== null}
            onClick={() => void download('days')}
          >
            {t('reports.promotionDiscounts.downloadDays', 'Download per-day CSV')}
          </Button>
        </div>
        <div className="overflow-x-auto rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('reports.promotionDiscounts.day', 'Business day')}</TH>
                <TH>{t('reports.promotionDiscounts.store', 'Store')}</TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.gross', 'Gross before discount')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.promotionDiscount', 'Promotion discount')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.otherDiscount', 'Other line discount')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.paymentDiscount', 'Discount at payment')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.unsplitDiscount', 'Discount not split')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.returned', 'Returned')}
                </TH>
                <TH className="text-right">{t('reports.promotionDiscounts.net', 'Net')}</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading && scope !== null && (
                <TR>
                  <TD colSpan={9} className="py-8 text-center text-muted-foreground">
                    {t('common.loading', 'Loading…')}
                  </TD>
                </TR>
              )}
              {data?.days.length === 0 && (
                <TR>
                  <TD colSpan={9} className="py-8 text-center text-muted-foreground">
                    {t('reports.promotionDiscounts.empty', 'No sales in the chosen days.')}
                  </TD>
                </TR>
              )}
              {data?.days.map((d) => (
                <TR
                  key={`${d.day}|${d.locationId ?? ''}|${d.currency ?? ''}`}
                  data-testid="promo-day-row"
                >
                  <TD className="font-mono text-xs">{d.day}</TD>
                  <TD className="text-sm">{store(d.locationCode, d.locationName)}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(d.grossMinor, d.currency)}
                  </TD>
                  <TD className="text-right font-medium tabular-nums">
                    {formatMinor(d.promotionDiscountMinor, d.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(d.otherDiscountMinor, d.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(d.paymentDiscountMinor, d.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(d.unsplitDiscountMinor, d.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(d.returnedMinor, d.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">{formatMinor(d.netMinor, d.currency)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>

      {/* ---------- Per item ---------- */}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {t('reports.promotionDiscounts.perItem', 'Per item')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            disabled={scope === null || downloading !== null}
            onClick={() => void download('items')}
          >
            {t('reports.promotionDiscounts.downloadItems', 'Download per-item CSV')}
          </Button>
        </div>
        <div className="overflow-x-auto rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('reports.promotionDiscounts.day', 'Business day')}</TH>
                <TH>{t('reports.promotionDiscounts.store', 'Store')}</TH>
                <TH>{t('receipts.field.sku', 'SKU')}</TH>
                <TH>{t('receipts.field.name', 'Name')}</TH>
                <TH className="text-right">{t('reports.promotionDiscounts.sold', 'Sold')}</TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.returnedQty', 'Returned qty')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.gross', 'Gross before discount')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.promotionDiscount', 'Promotion discount')}
                </TH>
                <TH className="text-right">
                  {t('reports.promotionDiscounts.otherDiscount', 'Other line discount')}
                </TH>
                <TH className="text-right">{t('reports.promotionDiscounts.net', 'Net')}</TH>
              </TR>
            </THead>
            <TBody>
              {data?.rows.length === 0 && (
                <TR>
                  <TD colSpan={10} className="py-8 text-center text-muted-foreground">
                    {t('reports.promotionDiscounts.empty', 'No sales in the chosen days.')}
                  </TD>
                </TR>
              )}
              {data?.rows.map((r) => (
                <TR
                  key={`${r.day}|${r.locationId ?? ''}|${r.currency ?? ''}|${r.sku ?? ''}|${r.name}|${r.unit}`}
                  data-testid="promo-item-row"
                >
                  <TD className="font-mono text-xs">{r.day}</TD>
                  <TD className="text-xs">{r.locationCode ?? '—'}</TD>
                  <TD className="font-mono text-xs">{r.sku ?? '—'}</TD>
                  <TD className="text-sm">{r.name}</TD>
                  <TD className="text-right tabular-nums">
                    {r.quantitySold}
                    {r.unit === 'each' ? '' : ` ${r.unit}`}
                  </TD>
                  <TD className="text-right tabular-nums">{r.quantityReturned}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(r.grossMinor, r.currency)}
                  </TD>
                  <TD className="text-right font-medium tabular-nums">
                    {formatMinor(r.promotionDiscountMinor, r.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(r.otherDiscountMinor, r.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">{formatMinor(r.netMinor, r.currency)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>

      <p className="text-xs text-muted-foreground" data-testid="promo-rules-note">
        {t(
          'reports.promotionDiscounts.rulesNote',
          'Refunds count on the day and in the store they were made, as returned quantity and returned money; a refund does not reverse the promotion discount of the original sale. Discount at payment is loyalty points, gift cards and cash rounding spread over the lines. Discount not split comes from tills that did not report where the discount came from.',
        )}
      </p>
    </div>
  );
}
